import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { clientContractsService, type ClientContractHistoryItem } from '@/features/client-contracts/services/client-contracts.service';
import { getClientDisplayName } from '@/features/clients/utils/display';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const Route = createFileRoute('/_authenticated/client-contracts/history')({
  component: ClientContractsHistoryPage,
});

type Snapshot = Record<string, unknown>;

function getActionLabel(action: ClientContractHistoryItem['action']) {
  if (action === 'create') return 'Criação';
  if (action === 'update') return 'Edição';
  return 'Exclusão';
}

function getActionVariant(action: ClientContractHistoryItem['action']) {
  if (action === 'create') return 'default' as const;
  if (action === 'update') return 'outline' as const;
  return 'destructive' as const;
}

function getSnapshot(changes: Record<string, unknown> | null | undefined) {
  if (!changes) return null;
  const after = changes.after as Snapshot | undefined;
  const before = changes.before as Snapshot | undefined;
  return after ?? before ?? null;
}

function parseMonthlyValue(value: unknown) {
  if (value == null) return '-';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return String(value);
  return `R$ ${parsed.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

function renderDiff(item: ClientContractHistoryItem) {
  const changes = item.changes ?? {};
  const before = (changes.before as Snapshot | undefined) ?? {};
  const after = (changes.after as Snapshot | undefined) ?? {};

  if (item.action !== 'update' || !changes.before || !changes.after) {
    return (
      <div className="text-xs text-muted-foreground">
        {item.action === 'delete'
          ? 'Snapshot completo do contrato registrado na exclusão.'
          : 'Registro de criação do contrato.'}
      </div>
    );
  }

  const watchedFields = [
    'monthlyValue',
    'adjustedAt',
    'nextAdjustmentDate',
    'startDate',
    'endDate',
    'currency',
  ];

  const changedFields = watchedFields.filter(
    (field) => before[field] !== after[field],
  );

  if (changedFields.length === 0) {
    return <div className="text-xs text-muted-foreground">Sem alterações rastreadas nos campos principais.</div>;
  }

  return (
    <div className="space-y-1">
      {changedFields.map((field) => (
        <div key={field} className="text-xs">
          <span className="font-medium">{field}: </span>
          <span className="text-muted-foreground">
            {field === 'monthlyValue'
              ? parseMonthlyValue(before[field])
              : String(before[field] ?? '-')}
          </span>
          <span className="mx-1">→</span>
          <span>
            {field === 'monthlyValue'
              ? parseMonthlyValue(after[field])
              : String(after[field] ?? '-')}
          </span>
        </div>
      ))}
    </div>
  );
}

function ClientContractsHistoryPage() {
  const navigate = useNavigate();
  const [contractFilter, setContractFilter] = useState('all');

  const { data: contractsList = [] } = useQuery({
    queryKey: ['client-contracts-list-for-history'],
    queryFn: () => clientContractsService.list(),
  });

  const { data: history = [], isLoading } = useQuery({
    queryKey: ['client-contracts-history', contractFilter],
    queryFn: () =>
      contractFilter === 'all'
        ? clientContractsService.listHistory()
        : clientContractsService.getContractHistory(contractFilter),
  });

  const contractOptions = useMemo(() => {
    return contractsList.map((item) => ({
      id: item.contract.id,
      label: `${getClientDisplayName(item.client)} • ${item.contract.id.slice(0, 8)}`,
    }));
  }, [contractsList]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: '/client-contracts', search: { clientId: undefined } })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Histórico de contratos</h1>
          <p className="text-muted-foreground">
            Acompanhe criação, edição, reajustes e exclusões dos contratos.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Select value={contractFilter} onValueChange={setContractFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por contrato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os contratos</SelectItem>
                {contractOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Eventos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading && <div className="text-muted-foreground">Carregando histórico...</div>}
          {!isLoading && history.length === 0 && (
            <div className="text-muted-foreground">Nenhum evento encontrado.</div>
          )}
          {!isLoading &&
            history.map((item) => {
              const snapshot = getSnapshot(item.changes);
              const contractId = String(snapshot?.id ?? item.entityId);
              return (
                <div key={item.id} className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={getActionVariant(item.action)}>
                        {getActionLabel(item.action)}
                      </Badge>
                      <span className="text-sm font-medium">Contrato {contractId.slice(0, 8)}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(item.createdAt), "dd/MM/yyyy 'às' HH:mm", {
                        locale: ptBR,
                      })}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Por: {item.changedByUser?.email ?? 'Sistema'}
                  </div>
                  {renderDiff(item)}
                </div>
              );
            })}
        </CardContent>
      </Card>
    </div>
  );
}
